import Draggable from 'vuedraggable';
import { VNodes } from "../../../../lib/utils/vue";
import { listComponentMixin } from "../../../../lib/helpers";
import { INJECT_UICORE_BUS, INJECT_UICORE_DESIGNER } from "../../../../lib/utils/consts";
import { startMouseMove, vueDeepAssign } from "../../../../lib/utils";
import { createNewColumn } from './getDefaultColumns';
const __script_base__ = {
  name: 'ArrayTable',
  mixins: [listComponentMixin],
  inject: {
    uc: {
      from: INJECT_UICORE_BUS,
      default: null
    },
    designer: {
      from: INJECT_UICORE_DESIGNER,
      default: null
    }
  },
  props: {
    cols: {
      type: Array,
      default: () => []
    },
    disableAdd: {
      type: Boolean,
      default: false
    },
    disableDelete: {
      type: Boolean,
      default: false
    },
    disableDrag: {
      type: Boolean,
      default: false
    },
    hideNumber: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: 0
    },
    minLength: {
      type: Number,
      default: 0
    },
    confirmBeforeDelete: {
      type: Boolean,
      default: false
    },
    handleDelete: {
      type: Function,
      default: null
    }
  },
  components: {
    Draggable,
    VNodes
  },
  data() {
    return {
      colOrder: [],
      colSize: {}
    };
  },
  computed: {
    canDragItem() {
      return !this.isDesignMode && !this.disableDrag && !this.finalReadonly;
    },
    colProps() {
      const ans = {};
      this.cols.forEach(col => {
        ans[col.id] = {
          'data-col-id': col.id,
          class: 'w-arrayTable-cell',
          style: `width: ${this.colSize[col.id] || col.width}`
        };
      });
      return ans;
    },
    getDefaultOperations() {
      const h = this.$createElement;
      const buttonGenerators = [];
      if (this.finalReadonly) return null;
      if (!this.disableDelete) {
        buttonGenerators.push(index => h("button", {
          "attrs": {
            "type": "button",
            "disabled": !!(this.minLength && this.arrayLength <= this.minLength)
          },
          "class": "ds-arrayTable-delete",
          "on": {
            "click": async () => {
              if (this.confirmBeforeDelete) {
                if (!(await this.uc.$confirm('确定要删除吗?'))) return;
              }
              if (typeof this.handleDelete === 'function') await this.handleDelete({
                index,
                item: this.array[index],
                list: this.array
              });
              this.deleteItem(index);
            }
          }
        }, ["\u5220\u9664"]));
      }
      if (!buttonGenerators.length) return null;
      return index => buttonGenerators.map(f => f(index));
    },
    sortedCols() {
      const {
        cols
      } = this;
      return this.colOrder.map(idx => ({
        ...cols[idx],
        originalIndex: idx
      }));
    }
  },
  methods: {
    validate() {
      if (this.maxLength > 0 && this.arrayLength > this.maxLength) throw new Error(`列表不能超过 ${this.maxLength} 条数据`);
      if (this.minLength && this.arrayLength < this.minLength) throw new Error(`列表至少要 ${this.minLength} 条数据`);
    },
    async handleHeaderDragEnd() {
      if (this.$attrs['data-uc-selected']) {
        // 设计模式下拖拽了！
        const newOrder = this.colOrder;
        await this.designer.modifyActiveComp(c => {
          c.props.cols = newOrder.map(idx => c.props.cols[idx]);
        });

        // 本地的 colOrder 不需要修改
        this.$nextTick(() => {
          this.colOrder = Array.from(newOrder, (_, i) => i);
        });
      }
    },
    /**
     * VueDraggable 会对 mousedown 做奇怪的逻辑，因此开始调整大小的功能，要挪到更上层捕获
     *
     * @param {MouseEvent} event
     */
    tryStartResizingCol(event) {
      let colId = '';
      const uBound = event.currentTarget;
      let colElement = event.target;
      if (!colElement.matches('.w-arrayTable-cellResizer')) return;
      while (colElement && colElement !== uBound) {
        colId = colElement.getAttribute('data-col-id');
        if (colId) break;
        colElement = colElement.parentNode;
      }
      if (!colId) return; // 没找到当前拖拽的列

      // ---------------------

      const oW = colElement.offsetWidth;
      this.$set(this.colSize, colId, '');
      event.stopPropagation();
      event.preventDefault();
      startMouseMove({
        initialEvent: event,
        onMove: ({
          deltaX
        }) => {
          this.colSize[colId] = `${oW + deltaX}px`;
        },
        onEnd: async () => {
          const newWidth = this.colSize[colId];

          // 设计模式，需要提交上去修改
          if (this.$attrs['data-uc-selected']) {
            this.$delete(this.colSize, colId, '');
            const compPath = this.$attrs['data-uc-path'];
            this.designer.compPath = compPath;
            await this.designer.modifyComp(compPath, c => {
              const {
                cols
              } = c.props;
              const idx = cols.findIndex(x => x.id === colId);
              this.$set(cols, idx, {
                ...cols[idx],
                width: newWidth
              });
            });
          }
        }
      });
    },
    // 设计模式

    async addColumn(event, position) {
      const col = createNewColumn();
      const compPath = this.$attrs['data-uc-path'];
      await this.designer.modifyComp(compPath, c => {
        var _c$props;
        if (!((_c$props = c.props) !== null && _c$props !== void 0 && _c$props.cols)) vueDeepAssign(c, {
          props: {
            cols: []
          }
        });
        c.props.cols.splice(position, 0, col);
      });
      this.$nextTick(() => this.resetColOrder());
    },
    // 设计模式

    async deleteColumn(index) {
      const compPath = this.$attrs['data-uc-path'];
      await this.designer.modifyComp(compPath, c => this.$delete(c.props.cols, index));
      this.$nextTick(() => this.resetColOrder());
    },
    // 其他

    resetColOrder() {
      const {
        length
      } = this.cols;
      this.colOrder = Array.from({
        length
      }, (_, i) => i);
      this.colSize = {};
    }
  },
  created() {
    this.$watch(() => this.cols.length, this.resetColOrder, {
      immediate: true
    });
  },
  provide() {
    return {
      'uicore:arrayTable': this,
      'uicore:arrayTable:isHeaderRow': false // RESET
    };
  }
};
var render = function () {
  var _vm = this;
  var _h = _vm.$createElement;
  var _c = _vm._self._c || _h;
  return _c('div', {
    staticClass: "w-arrayTable"
  }, [_c('div', {
    staticClass: "w-arrayTable-contentWrapper"
  }, [_c('div', {
    ref: "header",
    staticClass: "w-arrayTable-header"
  }, [_c('div', {
    staticClass: "w-arrayTable-rowWrapper",
    attrs: {
      "uc-designer-interactive": "true"
    },
    on: {
      "dragstart": function ($event) {
        $event.stopPropagation();
      },
      "!mousedown": function ($event) {
        return _vm.tryStartResizingCol.apply(null, arguments);
      },
      "!touchstart": function ($event) {
        return _vm.tryStartResizingCol.apply(null, arguments);
      }
    }
  }, [_c('draggable', {
    staticClass: "w-arrayTable-row",
    attrs: {
      "draggable": ".w-arrayTable-cell[data-col-id]",
      "filter": ".w-arrayTable-designerButtons, .w-arrayTable-cellResizer",
      "prevent-on-filter": false
    },
    on: {
      "end": _vm.handleHeaderDragEnd
    },
    model: {
      value: _vm.colOrder,
      callback: function ($$v) {
        _vm.colOrder = $$v;
      },
      expression: "colOrder"
    }
  }, [_vm._l(_vm.sortedCols, function (col) {
    return _c('div', _vm._b({
      key: col.id
    }, 'div', _vm.colProps[col.id], false), [_vm._v("\n            " + _vm._s(col.title) + "\n\n            "), col.description ? _c('a-tooltip', [_c('pre', {
      staticStyle: {
        "margin": "0"
      },
      attrs: {
        "slot": "title"
      },
      domProps: {
        "textContent": _vm._s(col.description)
      },
      slot: "title"
    }), _vm._v(" "), _c('a-icon', {
      attrs: {
        "type": "info-circle"
      }
    })], 1) : _vm._e(), _vm._v(" "), _vm.isDesignMode ? _c('div', {
      staticClass: "w-arrayTable-designerButtons"
    }, [_c('button', {
      staticClass: "w-arrayTable-designerButton",
      attrs: {
        "type": "button",
        "title": "添加列"
      },
      on: {
        "click": function ($event) {
          return _vm.addColumn($event, col.originalIndex);
        }
      }
    }, [_c('i', {
      staticClass: "fa fa-plus"
    })])]) : _vm._e(), _vm._v(" "), _vm.isDesignMode ? _c('div', {
      staticClass: "w-arrayTable-designerButtons atRight"
    }, [0 ? _c('button', {
      staticClass: "w-arrayTable-designerButton",
      attrs: {
        "type": "button",
        "title": "修改"
      },
      on: {
        "click": function ($event) {
          return _vm.showEditor($event, col.originalIndex);
        }
      }
    }, [_c('i', {
      staticClass: "fa fa-edit"
    })]) : _vm._e(), _vm._v(" "), _c('button', {
      staticClass: "w-arrayTable-designerButton",
      attrs: {
        "type": "button",
        "title": "删除"
      },
      on: {
        "click": function ($event) {
          return _vm.deleteColumn(col.originalIndex);
        }
      }
    }, [_c('i', {
      staticClass: "fa fa-trash"
    })])]) : _vm._e(), _vm._v(" "), _c('div', {
      staticClass: "w-arrayTable-cellResizer",
      attrs: {
        "uc-designer-interactive": "true"
      }
    })], 1);
  }), _vm._v(" "), !_vm.hideNumber ? _c('div', {
    staticClass: "w-arrayTable-cell isRowNumber",
    attrs: {
      "slot": "header"
    },
    slot: "header"
  }, [_vm._v("\n            No.\n          ")]) : _vm._e(), _vm._v(" "), _vm.getDefaultOperations || _vm.isDesignMode ? _c('div', {
    staticClass: "w-arrayTable-cell isOperation",
    staticStyle: {
      "width": "auto"
    },
    attrs: {
      "slot": "footer"
    },
    slot: "footer"
  }, [_vm.isDesignMode ? _c('div', {
    staticClass: "w-arrayTable-designerButtons"
  }, [_c('button', {
    staticClass: "w-arrayTable-designerButton",
    attrs: {
      "type": "button",
      "title": "添加列"
    },
    on: {
      "click": function ($event) {
        return _vm.addColumn($event, _vm.cols.length);
      }
    }
  }, [_c('i', {
    staticClass: "fa fa-plus"
  })])]) : _vm._e()]) : _vm._e()], 2)], 1)]), _vm._v(" "), _vm.emptySlotShown ? _c('div', {
    staticClass: "w-arrayTable-body"
  }, [_c('div', {
    ref: "emptySlot",
    staticClass: "w-arrayTable-emptySlot uc-slotForBlocks"
  }, [_vm._t("empty", function () {
    return [_c('div', {
      staticClass: "w-arrayTable-defaultEmpty"
    }, [_vm._v("\n            暂无内容\n          ")])];
  })], 2)]) : _c('draggable', {
    staticClass: "w-arrayTable-body",
    attrs: {
      "disabled": !_vm.canDragItem,
      "handle": ".w-arrayTable-dragHandle",
      "draggable": ".w-arrayTable-rowWrapper",
      "animation": 300
    },
    model: {
      value: _vm.array,
      callback: function ($$v) {
        _vm.array = $$v;
      },
      expression: "array"
    }
  }, _vm._l(_vm.itemRendererData, function (ref) {
    var slotProps = ref.slotProps;
    var index = ref.index;
    var key = ref.key;
    return _c('div', {
      key: key,
      ref: "arrayItemContainers",
      refInFor: true,
      staticClass: "w-arrayTable-rowWrapper",
      attrs: {
        "data-index": index
      }
    }, [_c('div', {
      staticClass: "w-arrayTable-row"
    }, [!_vm.hideNumber ? _c('div', {
      staticClass: "w-arrayTable-cell isRowNumber"
    }, [_c('div', {
      staticClass: "w-arrayTable-rowNumber",
      class: {
        'w-arrayTable-dragHandle': _vm.canDragItem
      }
    }, [_vm._v("\n              " + _vm._s(slotProps.data.num) + "\n            ")])]) : _vm._e(), _vm._v(" "), _vm._l(_vm.sortedCols, function (col) {
      return _c('div', _vm._b({
        key: col.id
      }, 'div', _vm.colProps[col.id], false), [_vm._t("col-" + col.id, null, null, slotProps)], 2);
    }), _vm._v(" "), _vm.getDefaultOperations ? _c('div', {
      staticClass: "w-arrayTable-cell isOperation",
      staticStyle: {
        "width": "auto"
      }
    }, [_c('div', {
      staticClass: "w-arrayTable-operations"
    }, [_c('VNodes', {
      attrs: {
        "vnodes": _vm.getDefaultOperations(index)
      }
    })], 1)]) : _vm._e()], 2)]);
  }), 0)], 1), _vm._v(" "), !_vm.finalReadonly && !_vm.disableAdd && (_vm.maxLength <= 0 || _vm.maxLength > _vm.arrayLength) ? _c('div', {
    ref: "footer",
    staticClass: "w-arrayTable-footer"
  }, [_c('button', {
    staticClass: "w-arrayTable-addButton",
    attrs: {
      "type": "button"
    },
    on: {
      "click": function ($event) {
        return _vm.addItem();
      }
    }
  }, [_c('i', {
    staticClass: "fa fa-plus"
  }), _vm._v(" 添加\n    ")])]) : _vm._e()]);
};
var staticRenderFns = [];
const __script__ = __script_base__;
export default Object.assign({
  render,
  staticRenderFns,
  _compiled: true
}, {
  "__file": "builtin/components/ArrayTable/index.vue"
}, typeof __script__ === 'function' ? __script__.options : __script__);